import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarcodeScanner } from './BarcodeScanner';
import { BarcodeInput } from './BarcodeInput';
import {
  useBarcodeScanner,
  parseBarcode,
  type BarcodeScanResult,
  type LookupResult,
} from '@/hooks/useBarcodeScanner';
import { toast } from 'sonner';
import {
  Camera,
  Keyboard,
  Check,
  X,
  AlertCircle,
  Package,
  Loader2,
} from 'lucide-react';
import type { MaterialInventory, RawMaterial } from '@/types/legacy';

interface BarcodeScannerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: {
    code: string;
    quantity: number;
    material?: MaterialInventory;
    rawMaterial?: RawMaterial;
    source: 'material_inventory' | 'raw_materials' | null;
  }) => void;
  // Filtrar solo para un tipo específico de material
  filterSource?: 'material_inventory' | 'raw_materials';
  title?: string;
}

/**
 * Dialog modal para escanear códigos de barras
 * Incluye tabs para cámara y entrada manual
 * Muestra preview del material encontrado
 */
export function BarcodeScannerDialog({
  open,
  onOpenChange,
  onConfirm,
  filterSource,
  title = 'Escanear Código de Barras',
}: BarcodeScannerDialogProps) {
  const { lookupMaterial } = useBarcodeScanner();
  const [activeTab, setActiveTab] = useState<'camera' | 'manual'>('camera');
  const [manualCode, setManualCode] = useState('');
  const [manualQuantity, setManualQuantity] = useState('');
  const [scanResult, setScanResult] = useState<BarcodeScanResult | null>(null);
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Buscar material cuando se escanea un código
  const handleScan = useCallback(
    async (result: BarcodeScanResult) => {
      setScanResult(result);
      setError(null);
      setIsLookingUp(true);

      try {
        const lookup = await lookupMaterial(result.code);

        // Si hay filtro de fuente, verificar que coincida
        if (filterSource && lookup.found && lookup.source !== filterSource) {
          setError(
            `El código corresponde a ${
              lookup.source === 'material_inventory'
                ? 'Inventario de Materiales'
                : 'Materias Primas'
            }, pero se esperaba ${
              filterSource === 'material_inventory'
                ? 'Inventario de Materiales'
                : 'Materias Primas'
            }`
          );
          setLookupResult(null);
        } else {
          setLookupResult(lookup);

          if (!lookup.found) {
            setError('Producto no encontrado en el sistema');
            toast.error('Producto no encontrado', {
              description: `El código "${result.code}" no existe en el sistema`,
            });
          } else {
            const materialName =
              lookup.material?.name || lookup.rawMaterial?.name;
            toast.success('Material encontrado', {
              description: `${result.code} - ${materialName}`,
            });
          }
        }
      } catch (err) {
        setError('Error al buscar el producto');
      } finally {
        setIsLookingUp(false);
      }
    },
    [lookupMaterial, filterSource]
  );

  // Manejar entrada manual
  const handleManualSubmit = useCallback(() => {
    const code = manualCode.trim();
    if (!code) {
      setError('Por favor ingresa un código');
      return;
    }

    const quantity = manualQuantity ? parseFloat(manualQuantity) : 0;
    const result: BarcodeScanResult = {
      code,
      quantity: isNaN(quantity) ? 0 : quantity,
      raw: manualQuantity ? `${code}|${manualQuantity}` : code,
    };

    handleScan(result);
  }, [manualCode, manualQuantity, handleScan]);

  // Confirmar selección
  const handleConfirm = useCallback(() => {
    if (!scanResult) return;

    // Usar la cantidad del lookup result si el material tiene cantidad definida
    const quantity = scanResult.quantity;

    onConfirm({
      code: scanResult.code,
      quantity,
      material: lookupResult?.material || undefined,
      rawMaterial: lookupResult?.rawMaterial || undefined,
      source: lookupResult?.source || null,
    });

    // Limpiar estado
    setScanResult(null);
    setLookupResult(null);
    setManualCode('');
    setManualQuantity('');
    setError(null);
    onOpenChange(false);
  }, [scanResult, lookupResult, onConfirm, onOpenChange]);

  // Limpiar y cerrar
  const handleClose = useCallback(() => {
    setScanResult(null);
    setLookupResult(null);
    setManualCode('');
    setManualQuantity('');
    setError(null);
    onOpenChange(false);
  }, [onOpenChange]);

  // Resetear para nuevo escaneo
  const handleReset = useCallback(() => {
    setScanResult(null);
    setLookupResult(null);
    setError(null);
  }, []);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Escanea un código de barras con la cámara o ingresa el código manualmente
          </DialogDescription>
        </DialogHeader>

        {/* Resultado del escaneo */}
        {scanResult && (
          <div className="space-y-3">
            {/* Datos escaneados */}
            <div className="p-3 rounded-lg bg-muted/50 border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Código escaneado:</span>
                <Badge variant="outline">{scanResult.code}</Badge>
              </div>
              {scanResult.quantity > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Cantidad:</span>
                  <Badge>{scanResult.quantity}</Badge>
                </div>
              )}
            </div>

            {/* Estado de búsqueda */}
            {isLookingUp && (
              <div className="flex items-center justify-center gap-2 py-4">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">
                  Buscando producto...
                </span>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive">
                <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Material encontrado */}
            {lookupResult?.found && (
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary">
                    Material encontrado
                  </span>
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {lookupResult.source === 'material_inventory'
                      ? 'Inventario'
                      : 'Materia Prima'}
                  </Badge>
                </div>

                {lookupResult.material && (
                  <div className="space-y-1 text-sm">
                    <p>
                      <strong>Nombre:</strong> {lookupResult.material.name}
                    </p>
                    {lookupResult.material.category && (
                      <p>
                        <strong>Categoría:</strong>{' '}
                        {lookupResult.material.category}
                      </p>
                    )}
                    <p>
                      <strong>Stock actual:</strong>{' '}
                      {lookupResult.material.current_stock}{' '}
                      {lookupResult.material.unit}
                    </p>
                  </div>
                )}

                {lookupResult.rawMaterial && (
                  <div className="space-y-1 text-sm">
                    <p>
                      <strong>Nombre:</strong> {lookupResult.rawMaterial.name}
                    </p>
                    <p>
                      <strong>Categoría:</strong>{' '}
                      {lookupResult.rawMaterial.category}
                    </p>
                    <p>
                      <strong>Cantidad:</strong>{' '}
                      {lookupResult.rawMaterial.quantity}{' '}
                      {lookupResult.rawMaterial.unit}
                    </p>
                    {lookupResult.rawMaterial.supplier && (
                      <p>
                        <strong>Proveedor:</strong>{' '}
                        {lookupResult.rawMaterial.supplier}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Botones de acción */}
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={handleReset}>
                Escanear otro
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={!scanResult}
              >
                <Check className="w-4 h-4 mr-2" />
                Confirmar
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Tabs para método de escaneo */}
        {!scanResult && (
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as 'camera' | 'manual')}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="camera" className="gap-2">
                <Camera className="w-4 h-4" />
                Cámara
              </TabsTrigger>
              <TabsTrigger value="manual" className="gap-2">
                <Keyboard className="w-4 h-4" />
                Manual
              </TabsTrigger>
            </TabsList>

            {/* Tab de cámara */}
            <TabsContent value="camera" className="mt-4">
              <BarcodeScanner
                onScan={handleScan}
                onError={(err) => setError(err)}
                showCloseButton={false}
                autoStart={activeTab === 'camera'}
              />
            </TabsContent>

            {/* Tab manual */}
            <TabsContent value="manual" className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="manual-code">Código del producto</Label>
                <BarcodeInput
                  onScan={handleScan}
                  onError={(err) => setError(err)}
                  placeholder="Escanear o escribir código..."
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  Escribe el código o usa un lector USB
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="manual-quantity">Cantidad (opcional)</Label>
                <Input
                  id="manual-quantity"
                  type="number"
                  step="0.001"
                  min="0"
                  value={manualQuantity}
                  onChange={(e) => setManualQuantity(e.target.value)}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="manual-code-input">O ingresa manualmente:</Label>
                <div className="flex gap-2">
                  <Input
                    id="manual-code-input"
                    type="text"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="MAT-001"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleManualSubmit();
                      }
                    }}
                  />
                  <Button onClick={handleManualSubmit}>Buscar</Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
